import React, { useMemo, useEffect, useState } from 'react';
import { Batiment, Personnel, Vehicule, Intervention } from '../../../../shared/types/game';
import { getPlanningServiceKeyFromBuildingType } from '../../utils/buildingServiceMapping';
import { DEFAULT_PLANNING_NOM, getPlanningLabelForLocalDate } from '../OperationalPlanningModal';
import {
  sortPersonnelInGroup,
  comparePlanningGroupsByPrio,
  resolvePlanningRowForLabel,
  planningBadgeCode,
  isPlanningPersonnelsJour,
} from './buildingDetailsTabHelpers';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Onglet « Vue opérationnelle » — port fidèle du prototype (`buildings.js` tabOps) :
 * 3 blocs — Personnels (groupés par planning du jour), Engins (état coloré +
 * légende), Interventions en cours + chantiers du site. Visuel .oscockpit.
 */

interface BuildingGererTabProps {
  building: Batiment;
  personnel: Personnel[];
  vehicules: Vehicule[];
  loadingPersonnel: boolean;
  loadingVehicules: boolean;
  onPersonnelAdded?: () => void;
  onVehiculeAdded?: () => void;
}

const vrowKind = (v: Vehicule): 'free' | 'busy' | 'off' =>
  v.statut === 'disponible' ? 'free' : v.statut === 'en_mission' ? 'busy' : 'off';
const vrowLab = (v: Vehicule): string =>
  v.statut === 'disponible'
    ? 'Disponible'
    : v.statut === 'en_mission'
      ? 'En mission'
      : v.statut === 'en_maintenance'
        ? 'Maintenance'
        : v.statut === 'panne'
          ? 'En panne'
          : 'Indisponible';

const BuildingGererTab: React.FC<BuildingGererTabProps> = ({
  building,
  personnel,
  vehicules,
  loadingPersonnel,
  loadingVehicules,
}) => {
  const [interventions, setInterventions] = useState<Intervention[]>([]);

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${getApiBaseUrl(true)}/buildings/interventions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setInterventions(data.data);
      } catch {
        /* silently ignore */
      }
    };
    load();
  }, [building.id]);

  const planningServiceKey = useMemo(
    () => getPlanningServiceKeyFromBuildingType(building.type),
    [building.type],
  );

  const personnelParPlanning = useMemo(() => {
    const today = new Date();
    const m = new Map<string, Personnel[]>();
    for (const p of personnel) {
      const key = String(getPlanningLabelForLocalDate(p, today) || DEFAULT_PLANNING_NOM).trim() || DEFAULT_PLANNING_NOM;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(p);
    }
    return Array.from(m.entries())
      .map(([k, v]) => [k, sortPersonnelInGroup(v)] as const)
      .sort(([a], [b]) => comparePlanningGroupsByPrio(a, b, planningServiceKey));
  }, [personnel, planningServiceKey]);

  const armables = useMemo(() => {
    const today = new Date();
    return personnel.filter((p) => p.actif && !isPlanningPersonnelsJour(p, today)).length;
  }, [personnel]);

  const vehiculeIdsDuBatiment = useMemo(() => new Set(vehicules.map((v) => Number(v.id))), [vehicules]);

  const interventionsActives = useMemo(() => {
    const bid = Number(building.id);
    return interventions.filter((i) => {
      if (i.statut !== 'en_cours' && i.statut !== 'en_attente') return false;
      if (Number(i.batiment_id) === bid) return true;
      return i.vehicules_ids?.some((vid) => vehiculeIdsDuBatiment.has(Number(vid))) ?? false;
    });
  }, [interventions, building.id, vehiculeIdsDuBatiment]);

  const dispoCount = vehicules.filter((v) => v.statut === 'disponible').length;
  const maintenanceVeh = vehicules.filter((v) => v.statut === 'en_maintenance' || v.statut === 'panne');
  const hasChantier =
    Boolean(building.augmentation_personnel_en_cours) ||
    Boolean(building.augmentation_vehicules_en_cours) ||
    maintenanceVeh.length > 0;

  return (
    <div className="bops">
      {/* ── Personnels ── */}
      <div className="block">
        <div className="block__h">
          Personnels <span className="mono muted">{personnel.length} · {armables} armables</span>
        </div>
        <div className="block__b">
          {loadingPersonnel ? (
            <div className="empty">Chargement…</div>
          ) : personnel.length === 0 ? (
            <div className="empty">Aucun personnel affecté à ce site.</div>
          ) : (
            personnelParPlanning.map(([lab, rows]) => {
              const pr = resolvePlanningRowForLabel(lab, planningServiceKey);
              const bg = pr?.couleurs.backgroundColor || '#3f3f46';
              const fg = pr?.couleurs.textColor || '#fafafa';
              return (
                <div className="pgroup" key={lab}>
                  <div className="pgroup__h" style={{ '--c': bg } as React.CSSProperties}>
                    <span className="pbadge" style={{ background: bg, color: fg }}>{planningBadgeCode(pr, lab)}</span>
                    <span className="pgroup__nom">{lab}</span>
                    <span className="pgroup__n mono">{rows.length}</span>
                  </div>
                  <div className="pgroup__b">
                    {rows.map((p) => (
                      <div
                        className={`pcard ${p.actif ? '' : 'is-off'}`}
                        key={p.id}
                        style={{ '--c': bg } as React.CSSProperties}
                        title={`${p.grade || ''} · GR-${p.groupe || '—'}`}
                      >
                        <span className="pcard__g">{p.grade_code || p.grade || '—'}</span>
                        <span className="pcard__n">{`${p.prenom || ''} ${p.nom || ''}`.trim()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Engins ── */}
      <div className="block">
        <div className="block__h">
          Engins <span className="mono muted">{vehicules.length} · {dispoCount} prêts</span>
        </div>
        <div className="block__b">
          {loadingVehicules ? (
            <div className="empty">Chargement…</div>
          ) : vehicules.length === 0 ? (
            <div className="empty">Aucun engin en remise.</div>
          ) : (
            <div className="vlist">
              {vehicules.map((v) => (
                <div className={`vrow vrow--${vrowKind(v)}`} key={v.id}>
                  <span className="vrow__call mono">{v.nom || `Engin ${v.id}`}</span>
                  <span className="vrow__type muted">{v.type}</span>
                  <span className="vrow__lab">{vrowLab(v)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="vlegend">
            <span><i className="dot dot--green" />prêt</span>
            <span><i className="dot dot--red" />non armé</span>
            <span><i className="dot dot--amber" />engagé</span>
            <span><i className="dot" style={{ background: '#64748b' }} />indisponible</span>
          </div>
        </div>
      </div>

      {/* ── Interventions en cours + chantiers ── */}
      <div className="block">
        <div className="block__h">
          Interventions en cours <span className="mono muted">{interventionsActives.length}</span>
        </div>
        <div className="block__b">
          {interventionsActives.length ? (
            <div className="ulist">
              {interventionsActives.map((i) => (
                <div className="urow" key={i.id}>
                  <span className="urow__call">{i.id}</span>
                  <span className="urow__grow">{i.type}{i.localisation ? ` — ${i.localisation}` : ''}</span>
                  <span className={`phase phase--${i.statut === 'en_cours' ? 'site' : 'wait'}`}>
                    {i.statut === 'en_cours' ? 'En cours' : 'En attente'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">Aucun moyen de ce site en intervention.</div>
          )}

          <p className="bsec">Chantiers du site</p>
          {hasChantier ? (
            <div className="ulist">
              {building.augmentation_personnel_en_cours ? (
                <div className="urow"><span className="urow__call">🏗</span><span className="urow__grow">Extension personnel</span></div>
              ) : null}
              {building.augmentation_vehicules_en_cours ? (
                <div className="urow"><span className="urow__call">🏗</span><span className="urow__grow">Extension remise</span></div>
              ) : null}
              {maintenanceVeh.map((v) => (
                <div className="urow" key={v.id}>
                  <span className="urow__call">🔧</span>
                  <span className="urow__grow">{v.nom || `Engin ${v.id}`} — {v.statut === 'panne' ? 'panne' : 'maintenance'}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">Aucun chantier en cours.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildingGererTab;
